import { analyticsAllowed, analyticsPath, analyticsToken, emitAnalyticsEvent, type AnalyticsParams } from "@/lib/analytics-events";

function locationParams(element: Element): AnalyticsParams {
  const section = element.closest<HTMLElement>("[data-analytics-section],section");
  return {
    cta_location: element.closest('[role="dialog"]') ? "dialog" : element.closest("header,nav") ? "navigation"
      : element.closest("footer") ? "footer" : element.closest("main") ? "main" : "page",
    section_id: analyticsToken(section?.dataset.analyticsSection || section?.id || section?.getAttribute("aria-labelledby")?.split(" ")[0]),
  };
}

/** One delegated listener set, recreated per navigation/consent period. No field values or text are read. */
export function installSiteTracking(): () => void {
  const started = new WeakSet<Element>();
  const used = new WeakSet<Element>();
  const depths = new Set<number>();
  let scrollFrame = 0;
  const element = (event: Event) => {
    const target = event.target instanceof Element ? event.target : null;
    return target?.closest("[data-analytics-ignore],[inert]") ? null : target;
  };

  const click = (event: MouseEvent) => {
    if (!analyticsAllowed() || event.button !== 0) return;
    const target = element(event);
    if (!target || target.closest('button:disabled,[aria-disabled="true"]')) return;
    const cta = target.closest<HTMLElement>("[data-book-call-trigger],.cc-btn,[data-analytics-cta],[data-campaign-cta]");
    if (cta && !cta.matches(':disabled,[aria-disabled="true"]')) {
      const params = { ...locationParams(cta), cta_type: cta.hasAttribute("data-book-call-trigger") || cta.dataset.analyticsCta === "booking" ? "booking"
        : cta.closest('form') && cta.matches('button[type="submit"]') ? "form_submit" : "action" };
      emitAnalyticsEvent("site_cta_click", params);
    }
    const campaignHook = target.closest<HTMLElement>("[data-campaign-cta]");
    if (campaignHook) emitAnalyticsEvent("campaign_cta_click", { cta_location: campaignHook.dataset.campaignCta });

    const link = target.closest<HTMLAnchorElement>("a[href]");
    if (!link) return;
    try {
      const url = new URL(link.href, window.location.href);
      const params = locationParams(link);
      if (url.protocol === "mailto:" || url.protocol === "tel:") {
        emitAnalyticsEvent("site_link_click", { ...params, link_type: url.protocol === "tel:" ? "phone" : "email" });
      } else if (url.protocol === "http:" || url.protocol === "https:") {
        const internal = url.origin === window.location.origin;
        emitAnalyticsEvent("site_link_click", { ...params,
          link_type: link.hasAttribute("download") || /\.(?:pdf|zip|docx?|xlsx?|pptx?)$/i.test(url.pathname) ? "download"
            : internal && url.pathname === window.location.pathname && url.hash ? "anchor" : internal ? "internal" : "external",
          link_domain: url.hostname,
          // External paths can contain identifiers; only retain paths within our site.
          ...(internal ? { link_path: analyticsPath(url.href) } : {}),
        });
      }
    } catch { /* Invalid/unsupported link. */ }
  };

  const focus = (event: FocusEvent) => {
    if (!analyticsAllowed()) return;
    const form = element(event)?.closest<HTMLElement>("form[data-analytics-form]");
    if (!form || started.has(form)) return;
    if (emitAnalyticsEvent("site_form_start", { form_id: form.dataset.analyticsForm, ...locationParams(form) })) {
      started.add(form);
      if (form.hasAttribute("data-campaign-form")) emitAnalyticsEvent("campaign_form_start");
    }
  };
  const submit = (event: SubmitEvent) => {
    const form = element(event);
    if (form?.matches("form[data-analytics-form]")) {
      emitAnalyticsEvent("site_form_attempt", { form_id: (form as HTMLElement).dataset.analyticsForm, ...locationParams(form) });
      if ((form as HTMLElement).dataset.analyticsForm === "blog_search") emitAnalyticsEvent("site_search");
    }
  };
  const tool = (event: Event) => {
    if (!analyticsAllowed()) return;
    const target = element(event);
    const root = target?.closest<HTMLElement>("[data-analytics-tool],[data-campaign-calculator]");
    if (!target || !root || used.has(root)) return;
    if (event.type === "pointerdown" && !target.closest('[role="slider"],input[type="range"]')) return;
    if (event instanceof KeyboardEvent && (!target.matches('[role="slider"],input[type="range"]')
      || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"].includes(event.key))) return;
    if (emitAnalyticsEvent("site_tool_use", { tool_id: root.dataset.analyticsTool ?? "campaign_calculator", ...locationParams(root) })) {
      used.add(root);
      if (root.hasAttribute("data-campaign-calculator")) emitAnalyticsEvent("campaign_calculator_use");
    }
  };
  const scroll = () => {
    if (scrollFrame || !analyticsAllowed()) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      const available = document.documentElement.scrollHeight - window.innerHeight;
      if (available <= 0) return;
      const percent = window.scrollY / available * 100;
      for (const depth of [25, 50, 75, 90]) {
        if (percent >= depth && !depths.has(depth) && emitAnalyticsEvent("site_scroll_depth", { percent_scrolled: depth })) depths.add(depth);
      }
    });
  };
  document.addEventListener("click", click, true);
  document.addEventListener("focusin", focus, true);
  document.addEventListener("submit", submit, true);
  document.addEventListener("input", tool, true);
  document.addEventListener("change", tool, true);
  document.addEventListener("pointerdown", tool, true);
  document.addEventListener("keydown", tool, true);
  window.addEventListener("scroll", scroll, { passive: true });
  return () => {
    document.removeEventListener("click", click, true);
    document.removeEventListener("focusin", focus, true);
    document.removeEventListener("submit", submit, true);
    document.removeEventListener("input", tool, true);
    document.removeEventListener("change", tool, true);
    document.removeEventListener("pointerdown", tool, true);
    document.removeEventListener("keydown", tool, true);
    window.removeEventListener("scroll", scroll);
    if (scrollFrame) cancelAnimationFrame(scrollFrame);
  };
}
